'use client';

import React, { useEffect, useRef, useState } from 'react';

export interface MoyasarCheckoutModalProps {
  presentation?: 'modal' | 'embedded';
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
  isOpen: boolean;
  onClose: () => void;
  paymentConfig: {
    amount: number;
    currency: string;
    description: string;
    callback_url: string;
    publishable_api_key: string;
    invoice_id?: string;
    apply_coupon?: boolean;
    apple_pay?: {
      country: string;
      label: string;
      validate_merchant_url: string;
      version?: number;
      merchant_capabilities?: (
        | 'supports3DS'
        | 'supportsEMV'
        | 'supportsCredit'
        | 'supportsDebit'
      )[];
      supported_countries?: string[];
      manual?: boolean;
      save_card?: boolean;
    };
    metadata?: Record<string, string>;
    supported_networks?: ('visa' | 'mastercard' | 'mada' | 'amex')[];
    methods?: ('creditcard' | 'applepay' | 'stcpay')[];
  };
  /**
   * Optional payment-method alternative (e.g. a native Apple Pay button)
   * rendered above the embedded card form with an "or pay with card" divider.
   * The slot owns its own payment lifecycle; the card form stays usable below.
   */
  applePaySlot?: React.ReactNode;
  onBeforePayment?: () => Promise<boolean>;
  onSuccess?: (paymentId: string) => void;
  onError?: (error: Error) => void;
}

declare global {
  interface Window {
    Moyasar?: {
      init: (config: Record<string, unknown>) => void;
    };
  }
}

/**
 * Moyasar Checkout Modal
 *
 * This component handles the Moyasar payment flow using their Form Javascript library.
 * It loads the Moyasar scripts and initializes the payment form within a modal.
 */
export function MoyasarCheckoutModal({
  presentation = 'modal',
  contentClassName,
  contentStyle,
  isOpen,
  onClose,
  paymentConfig,
  applePaySlot,
  onBeforePayment,
  onSuccess,
  onError,
}: MoyasarCheckoutModalProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const formContainerRef = useRef<HTMLDivElement>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsLoading(true);
      setError(null);
      // Clear form when modal is closed
      if (formContainerRef.current) {
        formContainerRef.current.innerHTML = '';
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;
    let formInitialized = false;
    const effectContainer = formContainerRef.current;
    let checkLoaded: ReturnType<typeof setInterval> | undefined;

    const loadAndInitialize = async () => {
      try {
        // Check if Moyasar script is already loaded
        if (!window.Moyasar) {
          // Check if script tag already exists
          let script = document.querySelector(
            'script[src*="moyasar-payment-form"]',
          ) as HTMLScriptElement;

          if (!script) {
            // Load CSS first and wait for it to load
            let cssLink = document.querySelector(
              'link[href*="moyasar.css"]',
            ) as HTMLLinkElement;

            if (!cssLink) {
              cssLink = document.createElement('link');
              cssLink.rel = 'stylesheet';
              cssLink.href =
                'https://cdn.jsdelivr.net/npm/moyasar-payment-form@2.2.0/dist/moyasar.css';

              // Wait for CSS to load before continuing
              await new Promise<void>((resolve, reject) => {
                cssLink.onload = () => resolve();
                cssLink.onerror = () =>
                  reject(new Error('Failed to load Moyasar CSS'));
                document.head.appendChild(cssLink);
              });
            }

            // Inject custom theme override to use app's primary color (only once)
            if (
              presentation === 'modal' &&
              !document.getElementById('moyasar-theme-override')
            ) {
              const bodyStyles = getComputedStyle(document.body);
              const primaryColor =
                bodyStyles.getPropertyValue('--primary').trim() ||
                getComputedStyle(document.documentElement)
                  .getPropertyValue('--primary')
                  .trim() ||
                '';

              const customStyles = document.createElement('style');
              customStyles.id = 'moyasar-theme-override';

              // Only override Moyasar button colors if a theme primary color is set
              const colorOverrides = primaryColor
                ? `
                /* Override button with app's primary color */
                div#mysr .bg-primary {
                  background-color: ${primaryColor} !important;
                }
                div#mysr .bg-primary:hover {
                  background-color: color-mix(in srgb, ${primaryColor} 85%, black) !important;
                }
                div#mysr .bg-primary:active {
                  background-color: color-mix(in srgb, ${primaryColor} 70%, black) !important;
                }
                div#mysr .bg-primary:disabled {
                  background-color: color-mix(in srgb, ${primaryColor} 50%, white) !important;
                  opacity: 0.7;
                }
                `
                : '';

              customStyles.textContent = `
                ${colorOverrides}
                /* Hide "Powered by Moyasar" branding */
                div#mysr footer,
                div#mysr .powered-by,
                div#mysr [class*="powered"],
                div#mysr a[href*="moyasar.com"] {
                  display: none !important;
                }
              `;
              document.head.appendChild(customStyles);
            }

            // Load JS
            script = document.createElement('script');
            script.src =
              'https://cdn.jsdelivr.net/npm/moyasar-payment-form@2.2.0/dist/moyasar.umd.min.js';

            // Wait for script to load
            await new Promise<void>((resolve, reject) => {
              script.onload = () => resolve();
              script.onerror = () =>
                reject(new Error('Failed to load Moyasar script'));
              document.head.appendChild(script);
            });
          } else if (!window.Moyasar) {
            // Script exists but not loaded yet, wait for it
            await new Promise<void>((resolve) => {
              checkLoaded = setInterval(() => {
                if (window.Moyasar) {
                  clearInterval(checkLoaded);
                  resolve();
                }
              }, 50);
            });
          }
        }

        if (!mounted || !formContainerRef.current || !window.Moyasar) return;

        // Avoid re-initializing if form already has content
        // This prevents the form from disappearing when tab visibility changes
        if (formContainerRef.current.children.length > 0) {
          setIsLoading(false);
          return;
        }

        const formContainer = formContainerRef.current;

        // Initialize Moyasar form
        window.Moyasar.init({
          element: formContainer,
          amount: paymentConfig.amount,
          currency: paymentConfig.currency,
          description: paymentConfig.description,
          publishable_api_key: paymentConfig.publishable_api_key,
          callback_url: paymentConfig.callback_url,
          ...(paymentConfig.invoice_id
            ? { invoice_id: paymentConfig.invoice_id }
            : {}),
          ...(paymentConfig.apply_coupon !== undefined
            ? { apply_coupon: paymentConfig.apply_coupon }
            : {}),
          ...(paymentConfig.apple_pay
            ? { apple_pay: paymentConfig.apple_pay }
            : {}),
          metadata: paymentConfig.metadata || {},
          supported_networks: paymentConfig.supported_networks || [
            'visa',
            'mastercard',
            'mada',
          ],
          methods: paymentConfig.methods || ['creditcard'],
          ...(onBeforePayment
            ? {
                on_initiating: async () => {
                  if (!formContainer.isConnected) return false;

                  try {
                    const allowed = await onBeforePayment();
                    return allowed && formContainer.isConnected;
                  } catch (error) {
                    onError?.(
                      error instanceof Error ? error : new Error(String(error)),
                    );
                    return false;
                  }
                },
              }
            : {}),
          on_completed: async (payment: { id: string; status: string }) => {
            // Step 3: Save payment ID (highly recommended by Moyasar docs)
            // This saves the payment before 3DS redirect in case connection drops
            try {
              console.log('Moyasar payment completed:', payment);

              // Call the success handler with payment details
              // This should save the payment ID to your backend
              await onSuccess?.(payment.id);

              // Note: After on_completed, Moyasar will redirect to callback_url with ?id=payment_id
              // The callback_url handler should verify the payment status
            } catch (error) {
              console.error('Error saving payment:', error);
              // Continue anyway - payment is already created
            }
          },
        });

        formInitialized = true;

        // Enable browser autofill for credit card fields rendered by Moyasar
        requestAnimationFrame(() => {
          const container = formContainerRef.current;
          if (!container) return;

          const selectors: Record<string, string> = {
            'input[name="number"], input[name="cardNumber"], input[placeholder*="card" i], input[placeholder*="Card" i]':
              'cc-number',
            'input[name="name"], input[placeholder*="name" i], input[placeholder*="Name" i]':
              'cc-name',
            'input[name="month"], input[placeholder*="MM" i]': 'cc-exp-month',
            'input[name="year"], input[placeholder*="YY" i]': 'cc-exp-year',
            'input[name="cvc"], input[name="cvv"], input[placeholder*="CVC" i], input[placeholder*="CVV" i]':
              'cc-csc',
          };

          for (const [selector, value] of Object.entries(selectors)) {
            container
              .querySelectorAll<HTMLInputElement>(selector)
              .forEach((el) => {
                el.setAttribute('autocomplete', value);
              });
          }
        });

        if (mounted) {
          setIsLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError('Failed to load payment form');
          setIsLoading(false);
          onError?.(err instanceof Error ? err : new Error(String(err)));
        }
      }
    };

    loadAndInitialize();

    return () => {
      mounted = false;
      clearInterval(checkLoaded);
      if (presentation === 'embedded' && effectContainer) {
        effectContainer.replaceChildren();
      }
      // Only clear the form when dialog is explicitly closed (not on tab switch)
      // We check if the form was initialized by this effect instance
      if (!isOpen && formInitialized && formContainerRef.current) {
        formContainerRef.current.innerHTML = '';
      }
    };
  }, [isOpen, paymentConfig, presentation, onBeforePayment, onSuccess, onError]);

  if (!isOpen) return null;

  const formContent = (
    <>
      {/* Apple Pay has no dependency on the embedded card widget, so it
          stays available even when the widget script fails to load
          (ad-blocker / CDN failure) — only the divider is tied to the
          card form being present. */}
      {applePaySlot && (
        <div className="mb-4">
          {applePaySlot}
          {!error && (
            <div className="mt-4 flex items-center gap-3">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs text-gray-500">or pay with card</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
          )}
        </div>
      )}

      {isLoading && (
        <div className="py-8 text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-gray-600"></div>
          <p className="text-gray-600">Loading payment form...</p>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Moyasar Form Container */}
      <div
        ref={formContainerRef}
        className={isLoading || error ? 'hidden' : ''}
      />
    </>
  );

  if (presentation === 'embedded') {
    return (
      <div data-moyasar-embedded className={contentClassName} style={contentStyle}>
        <style>{`
          [data-moyasar-embedded] div#mysr .bg-primary {
            background-color: var(--primary) !important;
          }
          [data-moyasar-embedded] div#mysr .bg-primary:hover {
            background-color: color-mix(in srgb, var(--primary) 85%, black) !important;
          }
          [data-moyasar-embedded] div#mysr .bg-primary:active {
            background-color: color-mix(in srgb, var(--primary) 70%, black) !important;
          }
          [data-moyasar-embedded] div#mysr .bg-primary:disabled {
            background-color: color-mix(in srgb, var(--primary) 50%, white) !important;
            opacity: 0.7;
          }
          [data-moyasar-embedded] div#mysr footer,
          [data-moyasar-embedded] div#mysr .powered-by,
          [data-moyasar-embedded] div#mysr [class*="powered"],
          [data-moyasar-embedded] div#mysr a[href*="moyasar.com"] {
            display: none !important;
          }
        `}</style>
        {formContent}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="bg-opacity-50 fixed inset-0 bg-black transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative w-full max-w-md rounded-2xl bg-white shadow-xl ${contentClassName ?? ''}`}
          style={contentStyle}
        >
          {/* Header */}
          <div className="border-b border-gray-200 p-6 pb-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  Payment
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Complete your payment securely
                </p>
              </div>
              <button
                type="button"
                aria-label="Close checkout"
                data-test="moyasar-checkout-close"
                onClick={onClose}
                className="-mt-1 -mr-1 text-gray-400 transition-colors hover:text-gray-500"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6">{formContent}</div>
        </div>
      </div>
    </div>
  );
}
